import {
  Alert,
  Button,
  Col,
  ConfigProvider,
  Divider,
  Flex,
  Form,
  FormInstance,
  Grid,
  Input,
  message,
  Row,
  Select,
  Tooltip,
  Typography
} from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import Plot from "react-plotly.js";

import { PlusOutlined, QuestionCircleOutlined, ReloadOutlined } from "@ant-design/icons";
import { createJob } from "api/job";
import { getCredentials, runComputation } from "api/playground";
import detector from "assets/detector.png";
import { LoadingSpinner } from "components/LoadingSpinner";
import { useUserType } from "hooks/useUserType";
import { COLOR_PRIMARY_BG, COLOR_PRIMARY_ORANGE } from "theme/colors";
import classes from "./Playground.module.css";
import { PLAYGROUND_TOOLTIP_CONTENT } from "constants/strings";
import { formatTime } from "modals/CreateJobModal";
import { useGetIdentity } from "@refinedev/core";
import { useSystemMaintenance } from "hooks/useSystemMaintenance";
import { MaintenanceAlert } from "components/MaintenanceAlert";
import { authProvider } from "authProvider";

type Values = {
  nPhotons: string;
  nModes: string;
  nEvents: string;
  unitaryType: string;
  jobName: string;
};

type Credentials = {
  maxModes: number;
  maxPhotons: number;
};

const MessageResponseBody = ({
  messageData,
  errorKey
}: {
  messageData: string;
  errorKey: string | number;
}) => {
  return (
    <>
      <span style={{ marginRight: 8 }}>{messageData}</span>
      <span>
        <Button type="primary" danger onClick={() => message.destroy(errorKey)}>
          Dismiss
        </Button>
      </span>
    </>
  );
};

const showErrorToast = (props: { messageData: string; errorKey: string }) => {
  message.error({
    content: <MessageResponseBody {...props} />,
    duration: 4,
    key: props.errorKey
  });
};

const LabelWithToolTip = ({ label, tooltipContent }: { label: string; tooltipContent: string }) => {
  return (
    <Flex gap={8}>
      <Typography.Text>{label}</Typography.Text>
      <Tooltip title={tooltipContent}>
        <QuestionCircleOutlined />
      </Tooltip>
    </Flex>
  );
};

export const Playground: React.FC = () => {
  const [values, setValues] = useState<Values>({
    nPhotons: "",
    nModes: "",
    nEvents: "",
    unitaryType: "",
    jobName: ""
  });
  const [showResult, setShowResult] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState({} as any);
  const [inputDisabled, setInputDisabled] = useState(false);
  const [loadingPlayGround, setLoadingPlayGround] = useState(false);
  const [credentialConfig, setCredentialConfig] = useState<Credentials | null>(null);
  const formRef = useRef<FormInstance | null>(null);
  const [priority, setPriority] = useState(5);
  const { isInternalUser, isAdminUser } = useUserType();
  const { refetch: refetchUser } = useGetIdentity();

  const { xs, sm, md, lg, xl } = Grid.useBreakpoint();
  const isSystemMaintenance = useSystemMaintenance();

  useEffect(() => {
    (async () => {
      setLoadingPlayGround(true);
      const response = await getCredentials();
      setCredentialConfig(response?.data.data[0]);
      setLoadingPlayGround(false);
    })();
  }, []);

  const hasErrors = useMemo(() => {
    const photons = Number(values.nPhotons);
    const modes = Number(values.nModes);
    const events = Number(values.nEvents);

    if (values.nPhotons.length > 0 && values.nModes.length > 0) {
      (async () => {
        try {
          await formRef.current?.validateFields(["photons", "modes"]);
        } catch (error) {}
      })();
    }

    if (values.nEvents.length > 0 && values.nModes.length > 0) {
      (async () => {
        try {
          await formRef.current?.validateFields(["modes", "events"]);
        } catch (error) {}
      })();
    }

    return (
      values.jobName.trim().length < 4 ||
      values.jobName.length > 255 ||
      Number.isNaN(photons) ||
      Number.isNaN(events) ||
      Number.isNaN(modes) ||
      photons < 2 ||
      photons > credentialConfig!.maxPhotons ||
      photons > modes ||
      modes > credentialConfig!.maxModes ||
      events < Math.max(100, Math.pow(Number(values.nModes), 2)) ||
      events > 10000 ||
      values.unitaryType.trim().length === 0
    );
  }, [values]);

  const run = async () => {
    const validateData = new Promise(function (resolve, reject) {
      Object.entries(values).forEach(([fieldName, fieldValue], index) => {
        // check if values for the fields exists
        if (fieldName !== "jobName" && fieldName !== "unitaryType") {
          if (!fieldValue && Number(fieldValue) !== 0) {
            showErrorToast({
              messageData: `Please fill the required field "Number of ${fieldName.replace("n", "")}"`,
              errorKey: index.toString()
            });
            reject();
          }
        }
        // check for fields nEvents, nPhotons, nModes, check if they are numbers
        // check if they are positive integers and check if they do not got contain scientific notation e
        if (
          (fieldName === "nEvents" || fieldName === "nPhotons" || fieldName === "nModes") &&
          (isNaN(Number(fieldValue)) || !Number.isInteger(Number(fieldValue)))
        ) {
          showErrorToast({
            messageData: `Please enter a positive integer value for "Number of ${fieldName.replace("n", "")}"`,
            errorKey: crypto.randomUUID()
          });
          reject();
        }
        // for nEvents field
        if (fieldName === "nEvents") {
          // nEvents limit: nEvents is > 100, < 10,000
          if (Number(values.nEvents) < 100 || Number(values.nEvents) > 10000) {
            const errormsg = `"Number of Events" cannot ${Number(values.nEvents) < 100 ? "be less than 100" : Number(values.nEvents) > 10000 ? "be more than 10,000" : ""}`;
            showErrorToast({
              messageData: errormsg,
              errorKey: crypto.randomUUID()
            });
            reject();
          }
        }
        // for nPhotons field
        if (fieldName === "nPhotons") {
          // nPhotons should be less than nModes
          if (Number(values.nPhotons) > Number(values.nModes)) {
            showErrorToast({
              messageData: `"Number of Photons" cannot be greater than "Number of Modes"`,
              errorKey: crypto.randomUUID()
            });
            reject();
          }
          if (Number(values.nPhotons) < 2) {
            showErrorToast({
              messageData: `"Number of Photons" cannot be less than 2`,
              errorKey: crypto.randomUUID()
            });
            reject();
          }
        }
      });
      resolve(true);
    });

    try {
      setLoading(true);
      const checkValidation = await validateData;
      if (checkValidation) {
        try {
          const res = await runComputation(values);
          if (res.error) {
            showErrorToast({
              messageData: res.message,
              errorKey: crypto.randomUUID()
            });
            return;
          }
          const data = {
            ...res.data.data,
            matrix: res.data.unitary
          };

          setResult(data);
          setShowResult(true);
          setInputDisabled(true);
        } catch (error) {
          setShowResult(false);
          showErrorToast({
            messageData: "Something went wrong while parsing the result",
            errorKey: crypto.randomUUID()
          });
        }
      }
      setLoading(false);
    } catch (e: any) {
      // validation failed
      console.log("Validation Error", e.message);
      return;
    } finally {
      setLoading(false);
    }
  };

  const handleCreateJob = async () => {
    try {
      setLoading(true);
      await createJob({
        job_name: `${values.jobName.trim()}`,
        job_type: "BS",
        n_photons: result.noOfPhotons,
        n_modes: result.noOfModes,
        max_events: [result.noOfEvents],
        max_time_ms: [0],
        unitary: result.matrix,
        configurations: [],
        priority: isInternalUser ? priority : undefined
      });
      authProvider
        .check()
        .then(() => refetchUser())
        .catch();
      setResult(null);
      setShowResult(false);
      setInputDisabled(false);
      formRef.current?.resetFields(["jobName"]);
      setValues({
        ...values,
        jobName: ""
      });
      message.success("Job created successfully");
    } catch (err: any) {
      showErrorToast({
        messageData: "An error occurred while creating the Job." + err.message,
        errorKey: crypto.randomUUID()
      });
      // console.log("Err:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loadingPlayGround) {
    return <LoadingSpinner />;
  }

  return (
    <div>
      {<MaintenanceAlert />}
      <Typography.Title style={{ fontWeight: "normal", fontSize: "30px" }} level={1}>
        Playground
      </Typography.Title>
      <Typography.Text>Enter the job details to get an estimate</Typography.Text>
      <Divider style={{ backgroundColor: "#404040" }} />
      <Typography.Title style={{ margin: 0, padding: 0 }} level={4}>
        Job details
      </Typography.Title>
      <Flex justify={"flex-start"} wrap="wrap" gap={xl ? 40 : 12}>
        <Flex
          style={{ ...(showResult ? { flexGrow: 1, maxWidth: "800px" } : { width: "400px" }) }}
          gap={12}>
          <Form ref={formRef} layout="vertical" style={{ width: "100%" }} requiredMark={false}>
            <Form.Item
              label={"Job Name"}
              name="jobName"
              colon={false}
              rules={[
                {
                  validator: (_, value: string) => {
                    if (value.trim().length <= 0) {
                      return Promise.reject("This field is required");
                    }
                    if (value.length > 255) {
                      return Promise.reject("You can enter a maximum of 255 characters");
                    }
                    if (value.trim().length < 4) {
                      return Promise.reject("Enter minimum of 4 characters");
                    }
                    return Promise.resolve();
                  }
                },
                {
                  required: true,
                  message: ""
                }
              ]}>
              <Input
                placeholder="Job Name"
                disabled={inputDisabled}
                onChange={(e) => {
                  setValues({
                    ...values,
                    jobName: e.target.value
                  });
                }}
              />
            </Form.Item>
            <Form.Item
              label={
                <LabelWithToolTip
                  label="Number of photons"
                  tooltipContent={PLAYGROUND_TOOLTIP_CONTENT.nPhotons}
                />
              }
              name="photons"
              colon={false}
              rules={[
                {
                  required: true,
                  message: "This field is required"
                }
              ]}>
              <Select
                disabled={inputDisabled}
                value={values.nPhotons}
                onChange={(e) => {
                  const newModes =
                    values.nModes && Number(values.nModes) < Number(e) ? e : values.nModes;
                  setValues({
                    ...values,
                    nPhotons: e,
                    nModes: newModes
                  });
                  formRef.current?.setFieldValue("modes", newModes);
                }}>
                {Array.from(
                  {
                    length: (credentialConfig?.maxPhotons || 2) - 1
                  },
                  (_, i) => (
                    <Select.Option key={i} value={i + 2}>
                      {i + 2}
                    </Select.Option>
                  )
                )}
              </Select>
            </Form.Item>
            <Form.Item
              label={
                <LabelWithToolTip
                  label="Number of modes"
                  tooltipContent={PLAYGROUND_TOOLTIP_CONTENT.nModes}
                />
              }
              name="modes"
              colon={false}
              rules={[
                {
                  required: true,
                  message: "This field is required"
                }
              ]}>
              {/* <Input disabled={true} defaultValue={12} /> */}
              <Select
                disabled={inputDisabled || values.nPhotons.length <= 0}
                value={values.nModes}
                onChange={(e) => {
                  setValues({
                    ...values,
                    nModes: e.toString()
                  });
                }}>
                {Array.from(
                  {
                    length: (credentialConfig?.maxModes || 0) - Number(values.nPhotons) + 1
                  },
                  (_, i) => (
                    <Select.Option key={i} value={i + Number(values.nPhotons)}>
                      {i + Number(values.nPhotons)}
                    </Select.Option>
                  )
                )}
              </Select>
            </Form.Item>
            <Form.Item
              label={
                <LabelWithToolTip
                  label="Unitary type"
                  tooltipContent={PLAYGROUND_TOOLTIP_CONTENT.unitaryType}
                />
              }
              name="unitaryType"
              colon={false}
              rules={[
                {
                  required: true,
                  message: "Select a value"
                }
              ]}>
              <Select
                disabled={inputDisabled}
                onChange={(e) => {
                  setValues({
                    ...values,
                    unitaryType: e
                  });
                }}>
                <Select.Option value="" disabled>
                  Select an option
                </Select.Option>
                <Select.Option value="eye">Eye</Select.Option>
                <Select.Option value="haar">Haar</Select.Option>
                <Select.Option value="fourier">Fourier</Select.Option>
                <Select.Option value="permutation">Permutation</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              normalize={(value) => (value?.toString() || "").replace(/[^0-9]/g, "")}
              label={
                <LabelWithToolTip
                  label="Number of events"
                  tooltipContent={PLAYGROUND_TOOLTIP_CONTENT.nEvents}
                />
              }
              name="events"
              colon={false}
              rules={[
                {
                  validator: (_, value: string) => {
                    const numValue = Number(value);
                    if (value && isNaN(numValue)) {
                      return Promise.reject("Please enter a valid number.");
                    }

                    const maxValue = Math.max(100, Math.pow(Number(values.nModes), 2));

                    if (value && value.length > 0 && numValue < maxValue) {
                      return Promise.reject(`Number of events can not be less than ${maxValue}.`);
                    }
                    if (numValue > 10000) {
                      return Promise.reject("Number of events can not be greater than 10,000.");
                    }
                    return Promise.resolve();
                  }
                },
                {
                  required: true,
                  message: "This field is required"
                }
              ]}>
              <Input
                placeholder="100"
                disabled={inputDisabled || values.nModes === ""}
                onChange={(e) => {
                  setValues({
                    ...values,
                    nEvents: e.target.value
                  });
                }}
              />
            </Form.Item>
            <Flex
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center"
              }}>
              <Button
                style={{
                  backgroundColor: "#0D111C",
                  border: "none",
                  height: "40px",
                  marginRight: "6px"
                }}
                onClick={() => {
                  setShowResult(false);
                  setResult({});
                  setValues({
                    ...values,
                    jobName: ""
                  });
                  formRef.current?.resetFields(["jobName"]);
                  setInputDisabled(false);
                }}
                disabled={!inputDisabled}>
                <ReloadOutlined />
                Reset
              </Button>
              <Button
                loading={!showResult && loading}
                style={{
                  backgroundColor: "#283583",
                  border: "none",
                  height: "40px",
                  marginLeft: "6px"
                }}
                onClick={run}
                disabled={
                  showResult ||
                  loading ||
                  formRef.current?.getFieldsError().some((value) => value.errors.length > 0) ||
                  hasErrors
                }>
                Run computation
              </Button>
            </Flex>
          </Form>
        </Flex>
        <Col
          className={classes.heatMapParent}
          style={{
            // marginRight: xl ? "unset" : "auto",
            // marginLeft: xl ? "unset" : "auto",
            marginTop: 12,
            maxWidth: "800px"
          }}>
          {showResult && (
            <>
              <div
                style={{
                  backgroundColor: COLOR_PRIMARY_BG,
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid #2F323B",
                  borderRadius: "12px"
                }}>
                <div className={classes.heatmap}>
                  <div className={classes.heatmapInput}>
                    <div className={classes.photons}>
                      {Array.from(
                        {
                          length: Number(values.nModes)
                        },
                        (_, i) => (
                          <div className={classes.photonCont} key={i}>
                            <div
                              style={{
                                visibility: i <= Number(values.nPhotons) - 1 ? "visible" : "hidden"
                              }}
                              className={classes.photon}
                            />
                            <div className={classes.dash} />
                          </div>
                        )
                      )}
                    </div>
                  </div>
                  <Plot
                    data={[
                      // {
                      //   z: result.matrix.map((row: any[]) => row.map((cell) => cell[0])),
                      //   type: "heatmap",
                      //   colorscale: [
                      //     [0, "transparent"],
                      //     [1, "transparent"]
                      //   ],
                      //   xgap: 1,
                      //   ygap: 1,
                      //   zmin: 0,
                      //   zmax: 1,
                      //   colorbar: {
                      //     x: 1.17,
                      //     tickcolor: "#fff",
                      //     tickfont: {
                      //       color: "#fff",
                      //       family: "Titillium Web",
                      //       size: 12
                      //     },
                      //     tickformat: ".2f"
                      //   }
                      // },
                      {
                        z: result.matrix.map((row: any[]) => row.map((cell) => cell[0])),
                        type: "heatmap",
                        colorscale: [
                          [0, "#283583"],
                          [1, COLOR_PRIMARY_ORANGE]
                        ],
                        xgap: 1,
                        ygap: 1,
                        zmin: 0,
                        zmax: 1,
                        colorbar: {
                          x: 1.17,
                          tickcolor: "#fff",
                          tickfont: {
                            color: "#fff",
                            family: "Titillium Web",
                            size: 12
                          },
                          tickformat: ".2f"
                        }
                      }
                    ]}
                    layout={{
                      width: 750,
                      height: 700,
                      showlegend: false,
                      xaxis: {
                        visible: false
                        // autorange: "reversed"
                      },
                      yaxis: {
                        visible: false,
                        autorange: "reversed"
                      },
                      plot_bgcolor: "transparent",
                      paper_bgcolor: "transparent"
                    }}
                    config={{
                      staticPlot: true,
                      setBackground: "transparent",
                      displayModeBar: false
                    }}
                  />
                  <div className={classes.detectors}>
                    {Array.from({ length: Number(values.nModes) }, (_, i) => (
                      <div className={classes.detectorCont} key={i}>
                        <span className={classes.detector} style={{ marginRight: "8px" }}>
                          {i + 1}
                        </span>
                        <img src={detector} alt="detector" className={classes.detector} />
                      </div>
                    ))}
                    {/* {Array.from({ length: Number(values.nModes) }, (_, i) => (
                      <div className={classes.detectorCont} key={i}>
                        <span className={classes.detector}>{i + 1}</span>
                      </div>
                    ))} */}
                  </div>
                </div>

                {(!isSystemMaintenance || isAdminUser) && (
                  <>
                    <div
                      role="separator"
                      style={{
                        height: "1px",
                        backgroundColor: "#475467",
                        width: "80%",
                        margin: "0 auto"
                      }}
                    />
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        margin: "0 40px",
                        justifyContent: "center"
                      }}>
                      <Row>
                        <Typography.Title
                          level={3}
                          style={{
                            fontWeight: 700,
                            fontSize: "20px",
                            lineHeight: "32px",
                            margin: "12px 0"
                          }}>
                          Job Estimate
                        </Typography.Title>
                      </Row>
                      <Row
                        style={{
                          marginBottom: "24px"
                        }}>
                        <>
                          <Flex gap={"24px"}>
                            <Col>
                              {" "}
                              <p className={classes.estimationHeading}>Estimated Run time </p>
                              <Typography.Paragraph>
                                <span className={classes.estimatedNumbers}>
                                  {formatTime(result.estimatedRunTime / 60)}{" "}
                                </span>
                                {/* <span className={classes.estimatedNumbersSubText}>minutes</span> */}
                              </Typography.Paragraph>
                            </Col>

                            <Col
                              style={{
                                width: "1px",
                                border: "none",
                                backgroundColor: "#E5E9EB",
                                marginBottom: "28px",
                                marginTop: "8px"
                              }}
                            />

                            <Col>
                              <p className={classes.estimationHeading}>Estimated Cost</p>
                              <Typography.Paragraph>
                                <span className={classes.estimatedNumbers}>
                                  {result.estimatedCost || 0}
                                </span>
                                <span className={classes.estimatedNumbersSubText}>credits</span>
                              </Typography.Paragraph>
                            </Col>
                          </Flex>
                        </>
                        <>
                          <Flex
                            align="center"
                            style={{
                              marginLeft: "auto",
                              padding: 0,
                              marginTop: "auto",
                              marginBottom: "auto"
                            }}>
                            {isInternalUser && (
                              <Flex align="middle" justify="center">
                                <p
                                  style={{
                                    padding: 0,
                                    margin: 0,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                  }}>
                                  Priority:
                                </p>
                                <Select
                                  variant="borderless"
                                  defaultValue={priority}
                                  onChange={(value) => setPriority(value)}>
                                  <Select.Option value={2}>2</Select.Option>
                                  <Select.Option value={5}>5</Select.Option>
                                </Select>
                              </Flex>
                            )}
                            <Button
                              loading={showResult && loading}
                              onClick={handleCreateJob}
                              style={{
                                width: "170px",
                                height: "44px",
                                border: "none"
                              }}>
                              <PlusOutlined />
                              Create job
                            </Button>
                          </Flex>
                        </>
                      </Row>
                    </div>
                  </>
                )}
              </div>
            </>
          )}
        </Col>
      </Flex>
      {/* <Row gutter={45}>
        
        
      </Row> */}
    </div>
  );
};
